<script setup lang="ts">
import { mdiLogin } from "@mdi/js";
import { useForm } from "@soft-db/form";
import { Button, FormField, Icon, TextInput } from "@soft-db/ui";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import AppTooltip from "../common/AppTooltip.vue";
import PasswordField from "../common/PasswordField.vue";

type LoginForm = {
	username: string;
	password: string;
};

const props = defineProps<{
	loading?: boolean;
}>();

const emit = defineEmits<{
	(e: "login", form: LoginForm);
}>();

const { useField, handleSubmit } = useForm<LoginForm>();

const username = useField("username", { isRequired: true });
const password = useField("password", { isRequired: true });

const login = handleSubmit((form) => emit("login", form));

const router = useRouter();

const { t } = useI18n();

const goToForgotPage = () => {
	router.push({ name: "auth-forgot" });
};
</script>

<template>
	<form class="relative" @submit="login">
		<div tabindex="-1" class="absolute flex w-full">
			<AppTooltip tabindex="-1">
				<span
					tabindex="-1"
					class="text-primary-content ml-auto mt-1.5 cursor-default pr-2 text-[11px] font-semibold underline"
				>
					{{ t("auth.forgotUsername") }}
				</span>
				<template #tooltip>
					<span>{{ t("auth.contact") }}</span>
				</template>
			</AppTooltip>
		</div>
		<FormField :label="t('common.usernameOrEmail')" :errors="username.errors.value">
			<TextInput
				v-model="username.data.value"
				class="bg-current/20 text-primary-content w-full"
				autocomplete="username"
				autofocus
			/>
		</FormField>

		<div tabindex="-1" class="absolute flex w-full">
			<Button
				tabindex="-1"
				variant="link"
				class="text-primary-content ml-auto mt-1 font-semibold"
				size="xs"
				@click="goToForgotPage"
			>
				{{ t("common.forgotPassword") }}
			</Button>
		</div>
		<PasswordField
			v-model="password.data.value"
			inputClass="bg-current/20 text-primary-content w-full"
			autocomplete="password"
			:label="t('common.password')"
		/>
		<div class="flex">
			<Button
				type="submit"
				class="w-38 mx-auto mt-4"
				color="default"
				size="md"
				:loading="props.loading"
			>
				{{ t("common.login") }}
				<Icon>{{ mdiLogin }}</Icon>
			</Button>
		</div>
	</form>
</template>
